import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Map, { Source, Layer, Popup, NavigationControl, AttributionControl, useControl } from 'react-map-gl/maplibre';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import StyleSwapControl from '../MapControl/StyleSwapControl';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'maplibre-gl/dist/maplibre-gl.css';
import { toast } from 'react-toastify';
import turf from '@turf/turf';
import bbox from '@turf/bbox';
import pointOnFeature from '@turf/point-on-feature';
import styles from './MapViewer.module.css';

// Импорт слоев
import { 
    getPlotLayer, 
    getBuildingLayer, 
    getRoadLayer, 
    getMarkerLayer, 
    getStatusIconsLayer,
    getPlotLabelLayer,
    getBuildingLabelLayer
} from './layers/mapLayers';

// Модули
import { modules as registryModules } from '../../generated-modules';

// Импорт иконок статусов
import { statusIcons, defaultMarkerIconSvg } from './statusIcons';

// Импорт всех инструментов
import { allTools } from '../../tools';
import { useDrawingTools } from '../../tools/drawing/useDrawingTools';
import { getFeatureCenter, computeDuplicatePlacement, createDuplicateFeature } from '../../tools/geometry/duplicatePlacementTool';

const LegendOverlay = registryModules.find(m => m.id === 'printing')?.LegendOverlay;

// Функции для создания паттернов штриховки
function createHorizontalHatch(size) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, size / 2);
    ctx.lineTo(size, size / 2);
    ctx.stroke();

    return canvas.toDataURL();
}

function createVerticalHatch(size) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(size / 2, 0);
    ctx.lineTo(size / 2, size);
    ctx.stroke();

    return canvas.toDataURL();
}

function createDiagonalHatch(size) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, size);
    ctx.lineTo(size, 0);
    ctx.stroke();

    return canvas.toDataURL();
}

function createCrossHatch(size) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = 1;

    // Горизонтальная линия
    ctx.beginPath();
    ctx.moveTo(0, size / 2);
    ctx.lineTo(size, size / 2);
    ctx.stroke();

    // Вертикальная линия
    ctx.beginPath();
    ctx.moveTo(size / 2, 0);
    ctx.lineTo(size / 2, size);
    ctx.stroke();

    return canvas.toDataURL();
}

function createDiagCrossHatch(size) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = 1;

    // Диагональ 1
    ctx.beginPath();
    ctx.moveTo(0, size);
    ctx.lineTo(size, 0);
    ctx.stroke();

    // Диагональ 2
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(size, size);
    ctx.stroke();

    return canvas.toDataURL();
}

 const MapViewer = ({
     initialViewState = {
         longitude: parseFloat(process.env.REACT_APP_MAP_LON) || 60.180497,
         latitude: parseFloat(process.env.REACT_APP_MAP_LAT) || 56.469595,
         zoom: parseFloat(process.env.REACT_APP_MAP_ZOOM) || 15.5,
         pitch: parseFloat(process.env.REACT_APP_MAP_PITCH) || 3.5,
         bearing: parseFloat(process.env.REACT_APP_MAP_BEARING) || 62.5
     },
     mapStyleId = 'basic',
     activeTool = null,
     onFeatureUpdate,
     onFeatureDelete,
     onFeatureCreate,
     onFeatureSelect,
     featureBeforeEdit,
     featureToEditGeometry,    
     onOverlayUpdate,
     mapData: mapDataProp, // Переименовано, чтобы избежать конфликта имен
     mapRef, // This is the ref to the Map component
     onDrawingControlsReady, // New prop to pass drawing controls back to parent
     legendData = {
         plots: [],
         buildings: [],
         roads: [],
         overlay: null,
         markers: []
     },
     activeFilters = {
         plots: [],
         buildings: [],
         roads: [],
         markers: [],
         status: ['for_sale', 'is_building', 'is_planned', 'no_status']
     },
     showStatusIcons = true,
     styleSwapControlProps = null,
     toolOptions = {},
     focusedFeature = null,
     onMapMove,
     restoreMapViewState = null,
     featureToCancel = null,
     onCancelComplete,    
     onRestoreComplete,
     onFeatureCancelEdit,
     onToolDeactivateRequest,
     printingSettings = null,
     mapSelectedIds = [],
     onShiftSelect,
     isShiftSelectionEnabled = false,
     duplicatingFeature = null,
     onDuplicatePlaced,
     onDuplicateCancelled
 }) => {
    const drawRef = useRef(null);
    const activeToolRef = useRef(null);
    const [mapIsLoaded, setMapIsLoaded] = useState(false);
    const [selectedFeatureId, setSelectedFeatureId] = useState(null);
    const [hoveredFeatureId, setHoveredFeatureId] = useState(null);
    const [popupInfo, setPopupInfo] = useState(null);
    const [overlay, setOverlay] = useState(legendData.overlay);
    const [isDrawing, setIsDrawing] = useState(false); // Новое состояние для режима рисования

    // Состояние для дублирования и перемещения объекта
    const [duplicatePlacement, setDuplicatePlacement] = useState(null);
    const [isShiftPressed, setIsShiftPressed] = useState(false);
    const mouseCoordRef = useRef(null);
    const duplicateAnchorRef = useRef(null);

    useEffect(() => {
        if (duplicatingFeature) {
            const anchor = getFeatureCenter(duplicatingFeature);
            duplicateAnchorRef.current = anchor;
            mouseCoordRef.current = anchor;
            setDuplicatePlacement({
                translatedFeature: duplicatingFeature,
                isSnapped: false,
                snapLine: null
            });
        } else {
            duplicateAnchorRef.current = null;
            mouseCoordRef.current = null;
            setDuplicatePlacement(null);
        }
    }, [duplicatingFeature]);

    // Обработка клавиш Shift, Enter, Escape для дублирования
    useEffect(() => {
        if (!duplicatingFeature) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Shift') {
                setIsShiftPressed(true);
                if (mouseCoordRef.current && duplicateAnchorRef.current) {
                    const result = computeDuplicatePlacement({
                        sourceFeature: duplicatingFeature,
                        anchorCoord: duplicateAnchorRef.current,
                        currentCoord: mouseCoordRef.current,
                        isShiftActive: true
                    });
                    setDuplicatePlacement(result);
                }
            } else if (e.key === 'Escape') {
                if (onDuplicateCancelled) onDuplicateCancelled();
            } else if (e.key === 'Enter') {
                if (duplicatePlacement && onDuplicatePlaced) {
                    const finalFeature = createDuplicateFeature(duplicatingFeature, duplicatePlacement.translatedFeature.geometry);
                    onDuplicatePlaced(finalFeature);
                }
            }
        };

        const handleKeyUp = (e) => {
            if (e.key === 'Shift') {
                setIsShiftPressed(false);
                if (mouseCoordRef.current && duplicateAnchorRef.current) {
                    const result = computeDuplicatePlacement({
                        sourceFeature: duplicatingFeature,
                        anchorCoord: duplicateAnchorRef.current,
                        currentCoord: mouseCoordRef.current,
                        isShiftActive: false
                    });
                    setDuplicatePlacement(result);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [duplicatingFeature, duplicatePlacement, onDuplicatePlaced, onDuplicateCancelled]);

    // Обработчик движения мыши при перемещении дубликата
    const handleMapMouseMove = useCallback((e) => {
        if (!duplicatingFeature || !duplicateAnchorRef.current) return;
        const currentCoord = [e.lngLat.lng, e.lngLat.lat];
        mouseCoordRef.current = currentCoord;
        const isShift = e.originalEvent?.shiftKey || isShiftPressed;

        const result = computeDuplicatePlacement({
            sourceFeature: duplicatingFeature,
            anchorCoord: duplicateAnchorRef.current,
            currentCoord,
            isShiftActive: isShift
        });
        setDuplicatePlacement(result);
    }, [duplicatingFeature, isShiftPressed]);

    // Инициализируем хук для инструментов рисования
     const { 
         startDrawing, 
         stopDrawing, 
         setOnDrawComplete, 
         clearDrawing, 
         startEditing, 
         saveEditing, 
         stopEditing, 
         undoVertex, 
         redoVertex, 
         clearAndRedraw,
         undoLastPoint,
         resetDrawing,
         completeDrawing
     } = useDrawingTools(mapRef.current, { 
          onFeatureCreate,
          onFeatureUpdate,
          onFeatureSelect,
          onFeatureCancelEdit,
          onToolDeactivate: onToolDeactivateRequest
      });

     // Стабилизируем объект с контролами рисования, чтобы он не пересоздавался при каждом рендере
     const drawingControls = useMemo(() => ({
         startDrawing,
         stopDrawing,
         onDrawComplete: setOnDrawComplete,
         clearDrawing,
         startEditing,
         saveEditing,
         stopEditing,
         undoVertex,
         redoVertex,
         clearAndRedraw,
         undoLastPoint,
         resetDrawing,
         completeDrawing,
         placeDuplicate: () => {
             if (duplicatingFeature && duplicatePlacement && onDuplicatePlaced) {
                 const finalFeature = createDuplicateFeature(duplicatingFeature, duplicatePlacement.translatedFeature.geometry);
                 onDuplicatePlaced(finalFeature);
             }
         }
     }), [startDrawing, stopDrawing, setOnDrawComplete, clearDrawing, startEditing, saveEditing, stopEditing, undoVertex, redoVertex, clearAndRedraw, undoLastPoint, resetDrawing, completeDrawing, duplicatingFeature, duplicatePlacement, onDuplicatePlaced]);

     // Pass drawing controls to parent once they are ready
     useEffect(() => {
         if (onDrawingControlsReady && drawingControls) {
             onDrawingControlsReady(drawingControls);
         }
     }, [onDrawingControlsReady, drawingControls]);

    // Управление инструментами
    useEffect(() => {
        if (!mapRef.current || !mapIsLoaded) return;

        const map = mapRef.current.getMap();

        // Деактивируем текущий инструмент
        if (activeToolRef.current) {
            activeToolRef.current.deactivate();
            activeToolRef.current.cleanup();
            activeToolRef.current = null;
        }

        // Активируем новый инструмент если указан
        if (activeTool && allTools[activeTool]) {
            // Устанавливаем флаг, если это инструмент рисования
            setIsDrawing(activeTool.startsWith('draw-'));

            const tool = allTools[activeTool];
            try {
                tool.initialize(map, {
                    onFeatureUpdate,
                    onFeatureDelete,
                    onOverlayUpdate: (newOverlay) => {
                        // Этот колбэк обновляет состояние подложки в реальном времени
                        setOverlay(newOverlay);
                        if (onOverlayUpdate) onOverlayUpdate(newOverlay);
                    },
                    legendData,
                    activeFilters,
                    options: toolOptions[activeTool] || {}, 
                    ...toolOptions[activeTool], // Передаем специфичные для инструмента опции (например, onCoordsSelect)
                    drawingControls,
                    focusedFeature: focusedFeature, // Передаем редактируемый объект в инструмент
                });
                
                // Определяем тип объекта на основе ID инструмента
                let objectType = null;
                let defaultProperties = {};
                if (activeTool.startsWith('draw-')) {
                    objectType = activeTool.substring(5); // 'draw-plot' -> 'plot', 'draw-line' -> 'line'
                    // Устанавливаем базовые свойства, включая тип, которые будут добавлены к объекту при создании
                    defaultProperties = {
                        type: objectType,
                        color: '#D20C0C' // Временный цвет для предпросмотра
                    };
                }
                // Передаем определенный тип в метод activate
                // и свойства по умолчанию
                tool.activate(objectType, defaultProperties);

                activeToolRef.current = tool;
                console.log(`[MapViewer] Инструмент "${activeTool}" активирован`);
            } catch (error) {
                console.error(`[MapViewer] Ошибка инициализации инструмента "${activeTool}":`, error);
            }
        } else {
            setIsDrawing(false); // Сбрасываем флаг, если инструмент неактивен
        }

        return () => {
            if (activeToolRef.current) {
                activeToolRef.current.deactivate();
                activeToolRef.current.cleanup();
                activeToolRef.current = null;
            }
            setIsDrawing(false); // Сбрасываем флаг при очистке
        };
    }, [activeTool, mapIsLoaded, onFeatureUpdate, onFeatureDelete, onOverlayUpdate, legendData, activeFilters, toolOptions, drawingControls, focusedFeature]);

    // Эффект для редактирования геометрии существующего объекта
    useEffect(() => {
        if (featureToEditGeometry && drawingControls) {
            drawingControls.startEditing(featureToEditGeometry);
        } else if (!featureToEditGeometry && drawingControls) {
            drawingControls.stopEditing();
        }
    }, [featureToEditGeometry, drawingControls]);

    // Эффект для фокусировки на созданном или выбранном объекте.
    // Не срабатывает, если мы только что закончили редактировать геометрию (т.к. фокус уже на месте).
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (map && focusedFeature && focusedFeature.geometry && !featureToEditGeometry && !featureBeforeEdit) {
            try {
                console.log("Fitting bounds for focused feature:", focusedFeature.id);
                const featureBbox = bbox(focusedFeature);
                const paddingRight = typeof window !== 'undefined' && window.innerWidth > 768 
                    ? Math.min(420, Math.floor(window.innerWidth * 0.35)) 
                    : 40;

                map.fitBounds(featureBbox, {
                    padding: {
                        top: 60,
                        bottom: 60,
                        left: 60,
                        right: paddingRight // Ширина панели редактирования + отступ
                    },
                    maxZoom: 17,
                    duration: 1000
                });
            } catch (e) {
                console.error("Не удалось сфокусироваться на объекте:", e);
            }
        }
    }, [focusedFeature, featureToEditGeometry, featureBeforeEdit]);

    // Эффект для обновления данных на карте без перезагрузки
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (map && map.isStyleLoaded() && mapDataProp) {
            const source = map.getSource('park-data');
            if (source) {
                source.setData(mapDataProp);
            }
        }
    }, [mapDataProp]);

    // Эффект для удаления временного объекта при отмене
    useEffect(() => {
        if (featureToCancel && drawRef.current?.get(featureToCancel)) {
            drawRef.current.delete(featureToCancel);
        }
        if (onCancelComplete) {
            onCancelComplete();
        }
    }, [featureToCancel, onCancelComplete]);

    // Эффект для восстановления предыдущего состояния карты
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (map && restoreMapViewState) {
            map.flyTo({
                center: [restoreMapViewState.longitude, restoreMapViewState.latitude],
                zoom: restoreMapViewState.zoom,
                bearing: restoreMapViewState.bearing,
                pitch: restoreMapViewState.pitch,
                duration: 1000 // Плавная анимация
            });
            if (onRestoreComplete) onRestoreComplete();
        }
    }, [restoreMapViewState, onRestoreComplete]);

    // Стиль карты
    const mapStyle = useMemo(() => {
        if (mapStyleId === 'satellite') {
            return {
                version: 8,
                name: "ESRI Satellite",
                sources: {
                    'esri-world-imagery': {
                        type: 'raster',
                        tiles: ['https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
                        tileSize: 256,
                        attribution: 'Tiles © Esri'
                    },
                    'esri-world-labels': {
                        type: 'raster',
                        tiles: ['https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'],
                        tileSize: 256
                    }
                },
                layers: [
                    { id: 'background', type: 'background', paint: { 'background-color': '#111' } },
                    { id: 'imagery-layer', type: 'raster', source: 'esri-world-imagery' },
                    { id: 'labels-layer', type: 'raster', source: 'esri-world-labels' }
                ]
            };
        }
        if (mapStyleId === 'custom') {
            // Просто возвращаем URL к файлу стилей, который предоставляет ваш тайловый сервер.
            return '/tiles/styles/custom/style.json';
        }
        return '/style.json';
    }, [mapStyleId]);

    // Фильтры для слоев
    const plotConditions = useMemo(() => {
        const plots = Array.isArray(activeFilters.plots) ? activeFilters.plots : [];
        if (plots.length === 0) return [['==', 'category', '___none___']]; // Прячем всё, если категории не выбраны
        return [['any', ['in', 'category', ...plots], ['!has', 'category'], ['==', 'category', '']]];
    }, [activeFilters.plots]);

    const buildingConditions = useMemo(() => {
        const buildings = Array.isArray(activeFilters.buildings) ? activeFilters.buildings : [];
        if (buildings.length === 0) return [['==', 'category', '___none___']];
        return [['any', ['in', 'category', ...buildings], ['!has', 'category'], ['==', 'category', '']]];
    }, [activeFilters.buildings]);

    const roadConditions = useMemo(() => {
        const roads = Array.isArray(activeFilters.roads) ? activeFilters.roads : [];
        if (roads.length === 0) return [['==', 'category', '___none___']];
        return [['any', ['in', 'category', ...roads], ['!has', 'category'], ['==', 'category', '']]];
    }, [activeFilters.roads]);

    const markerConditions = useMemo(() => {
        const markers = Array.isArray(activeFilters.markers) ? activeFilters.markers : [];
        if (markers.length === 0) return [['==', 'category', '___none___']];
        return [['any', ['in', 'category', ...markers], ['!has', 'category'], ['==', 'category', '']]];
    }, [activeFilters.markers]);

    // Статусный фильтр
    const statusConditions = useMemo(() => {
        const statusList = Array.isArray(activeFilters.status) ? activeFilters.status : [];
        
        // Если ничего не выбрано в статусах, значит мы не хотим видеть объекты со статусами (или вообще объекты)
        // На публичной карте это обычно означает "скрыть всё"
        if (statusList.length === 0) return [['==', 'id', '___none___']];

        const conditions = [];

        if (statusList.includes('for_sale')) {
            conditions.push(['==', 'for_sale', true]);
        }
        if (statusList.includes('is_building')) {
            conditions.push(['==', 'is_building', true]);
        }
        if (statusList.includes('is_planned')) {
            conditions.push(['==', 'is_planned', true]);
        }
        if (statusList.includes('no_status')) {
            conditions.push(['all',
                ['any', ['!has', 'for_sale'], ['==', 'for_sale', false]],
                ['any', ['!has', 'is_building'], ['==', 'is_building', false]],
                ['any', ['!has', 'is_planned'], ['==', 'is_planned', false]]
            ]);
        }

        if (conditions.length === 0) {
            return [['==', 'id', '___none___']];
        } else if (conditions.length === 1) {
            return [conditions[0]];
        } else {
            return [['any', ...conditions]];
        }
    }, [activeFilters.status]);

    // Создание источника иконок статусов
    const statusIconsSource = useMemo(() => {
        if (!mapDataProp || !mapDataProp.features) return { type: 'FeatureCollection', features: [] };

        const iconPoints = [];
        mapDataProp.features.forEach(feature => {
            // Если объект скрыт, не создаем для него иконки статусов
            if (feature.properties.visible === false) return;

            const activeStatuses = [];
            if (feature.properties.for_sale) activeStatuses.push({ type: 'for_sale', icon: 'for-sale-icon' });
            if (feature.properties.is_building) activeStatuses.push({ type: 'is_building', icon: 'is-building-icon' });
            if (feature.properties.is_planned) activeStatuses.push({ type: 'is_planned', icon: 'is-planned-icon' });

            if (activeStatuses.length > 0) {
                try {
                    // Используем pointOnFeature для более точного центра, особенно для полигонов и линий
                    const featureCenter = pointOnFeature(feature).geometry.coordinates;
                    const [lon, lat] = featureCenter;

                    // Определяем динамическое смещение на основе размеров объекта
                    const [minX, , maxX] = bbox(feature.geometry);
                    const x_spacing = (maxX - minX) * 0.15; // Расстояние между иконками, пропорционально ширине объекта
                    const totalWidth = (activeStatuses.length - 1) * x_spacing; // Общая ширина, занимаемая иконками
                    const startLon = lon - totalWidth / 2; // Начальная долгота для размещения иконок

                    activeStatuses.forEach((status, index) => {
                        iconPoints.push({
                            type: 'Feature',
                            geometry: { type: 'Point', coordinates: [startLon + (index * x_spacing), lat] },
                            properties: {
                                id: feature.id,
                                icon_image: status.icon,
                                type: feature.properties.type,
                                category: feature.properties.category,
                                for_sale: feature.properties.for_sale,
                                is_building: feature.properties.is_building,
                                is_planned: feature.properties.is_planned
                            }
                        });
                    });
                } catch (e) {
                    console.warn(`Не удалось создать иконку статуса для ${feature.id}:`, e.message);
                }
            }
        });

        return { type: 'FeatureCollection', features: iconPoints };
    }, [mapDataProp, showStatusIcons]);

    // Загрузка кастомных иконок
    const onMapLoad = useCallback((evt) => {
        const map = evt.target;
        if (!map) return;

        // Загрузка иконки метки по умолчанию
        const defaultMarkerImage = new Image(32, 32);
        defaultMarkerImage.onload = () => {
            if (!map.hasImage('default-marker')) {
                map.addImage('default-marker', defaultMarkerImage, { sdf: false });
            }
        };
        defaultMarkerImage.onerror = () => console.error('Не удалось загрузить иконку метки по умолчанию.');
        defaultMarkerImage.src = `data:image/svg+xml;base64,${btoa(defaultMarkerIconSvg)}`;


        // Загрузка иконок статусов
        Object.entries(statusIcons).forEach(([id, svg]) => {
            const image = new Image(32, 32);
            image.onload = () => {
                if (!map.hasImage(id)) {
                    map.addImage(id, image, { sdf: false });
                }
            };
            image.src = `data:image/svg+xml;base64,${btoa(svg)}`;
        });

        // Регистрация паттернов для штриховки
        const registerHatchPatterns = () => {
            const map = evt.target;
            if (!map) return;
            const patternSize = 8;
            const patterns = {
                'horizontal': createHorizontalHatch(patternSize),
                'vertical': createVerticalHatch(patternSize),
                'diagonal': createDiagonalHatch(patternSize),
                'cross': createCrossHatch(patternSize),
                'diagcross': createDiagCrossHatch(patternSize)
            };

            Object.entries(patterns).forEach(([name, dataUrl]) => {
                if (!map.hasImage(`hatch-${name}`)) {
                    const img = new Image();
                    img.onload = () => {
                        map.addImage(`hatch-${name}`, img);
                    };
                    img.src = dataUrl;
                }
            });
        };

        registerHatchPatterns();

        setMapIsLoaded(true);
    }, [legendData]);

    // Эффект для загрузки изображений меток при изменении legendData
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !mapIsLoaded) return;

        const loadMarkerImages = async () => {
            try {
                const markersToLoad = (legendData.markers || []).filter(
                    marker => marker.image_url && marker.image_filename && !map.hasImage(marker.image_filename)
                );

                if (markersToLoad.length === 0) return;

                const promises = markersToLoad.map(marker => {
                    return new Promise((resolve) => {
                        const image = new Image();
                        image.crossOrigin = 'anonymous'; // Разрешаем cross-origin загрузку
                        image.onload = () => {
                            map.addImage(marker.image_filename, image, { sdf: false });
                            resolve();
                        };
                        image.onerror = (e) => {
                            console.error(`Ошибка загрузки метки ${marker.image_filename}:`, e);
                            resolve(); // Не прерываем всю загрузку, если одна иконка не загрузилась
                        };
                        image.src = marker.image_url;
                    });
                });
                await Promise.all(promises);
                console.log('Новые иконки меток загружены.');
            } catch (error) {
                console.error('Ошибка загрузки изображений меток:', error);
            }
        };
        loadMarkerImages();
    }, [legendData, mapIsLoaded]);

    // Обработчик клика по объекту
    const handleFeatureClick = useCallback((e) => {
        const feature = e.features?.[0];
        if (feature && onFeatureSelect) {
            onFeatureSelect(feature);
        }
    }, [onFeatureSelect]);

    // Обработчик для touch событий (мобильные устройства)
    const handleFeatureTap = useCallback((e) => {
        const feature = e.features?.[0];
        if (feature && onFeatureSelect) {
            onFeatureSelect(feature);
        }
    }, [onFeatureSelect]);

    // Глобальный обработчик клика по карте для предотвращения нежелательных действий
    const handleMapClick = useCallback((e) => {
        // Если активен режим дублирования объекта — ставим копию на карту
        if (duplicatingFeature) {
            if (duplicatePlacement && onDuplicatePlaced) {
                const finalFeature = createDuplicateFeature(duplicatingFeature, duplicatePlacement.translatedFeature.geometry);
                onDuplicatePlaced(finalFeature);
            }
            return;
        }

        // Если клик пришелся по слою-перехватчику, ничего не делаем.
        if (e.features && e.features.some(f => f.layer.id === 'drawing-interceptor-layer')) {
            return;
        }

        // Обработка Shift+Click для массового выделения
        if (isShiftSelectionEnabled && e.originalEvent.shiftKey && e.features && e.features.length > 0) {
            const feature = e.features[0];
            const featureId = feature.id || feature.properties?.id;
            if (featureId && onShiftSelect) {
                onShiftSelect(featureId);
            }
            return; // Предотвращаем обычный выбор при зажатом Shift
        }

        // Вызываем выбор объекта, только если не активен никакой инструмент и не идет редактирование геометрии.
        // Это предотвращает случайный выбор объекта при использовании инструментов рисования или редактирования вершин.
        if (!activeTool && !isDrawing && !featureToEditGeometry && e.features && e.features.length > 0) {
            handleFeatureClick(e);
        }
    }, [duplicatingFeature, duplicatePlacement, onDuplicatePlaced, activeTool, handleFeatureClick, isDrawing, featureToEditGeometry, isShiftSelectionEnabled, onShiftSelect]);

    // Обработчик наведения на объект
    const handleMouseEnter = useCallback((e) => {
        const feature = e.features[0];
        if (feature && feature.properties) {
            setHoveredFeatureId(feature.properties.id);
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        setHoveredFeatureId(null);
    }, []);

    // Определяем, является ли объект новым (еще не сохранен в mapDataProp)
    const isNewFeature = useMemo(() => {
        if (!focusedFeature) return false;
        return !mapDataProp?.features?.some(f => f.id === focusedFeature.id);
    }, [focusedFeature, mapDataProp]);

    // Слои для отображения
    const layers = useMemo(() => {
        if (!mapDataProp && !focusedFeature) return [];

        const layersConfig = [];

        // Собираем общие условия для слоев с данными
        const dataLayerConditions = statusConditions || [];


        // Слой участков
        layersConfig.push(...getPlotLayer([...plotConditions, ...dataLayerConditions]));

        // Слой для принудительного отображения очень больших участков
        // Он будет под всеми слоями, но над подложкой (overlay)
        // Учитывает те же категории, что и обычные участки
        layersConfig.push({
            id: 'large-plots-force-render-layer',
            type: 'fill',
            source: 'park-data',
            filter: ['all', ['==', 'type', 'plot'], ['>', 'area', 100000], ...plotConditions],
            paint: {
                'fill-color': ['get', 'color'],
                'fill-opacity': 0.5
            },
            beforeId: 'plots-layer-solid' // Размещаем этот слой прямо перед слоем обычных участков
        });

        // Слой дорог (поверх участков, но под зданиями)
        layersConfig.push(...getRoadLayer([...roadConditions, ...dataLayerConditions]));

        // Слой зданий (3D) (поверх дорог)
        layersConfig.push(
            ...getBuildingLayer([...buildingConditions, ...dataLayerConditions], '3d')
        );
        

        // Слой меток
        // Аналогично для меток
        layersConfig.push(getMarkerLayer([...markerConditions, ...dataLayerConditions]));

        // Слой иконок статусов
        if (showStatusIcons && statusIconsSource.features.length > 0) {
            layersConfig.push(getStatusIconsLayer(dataLayerConditions));
        }

        // --- МОДУЛЬ ПЕЧАТИ (ПРИМЕНЕНИЕ НАСТРОЕК) ---
        if (printingSettings) {
            const { text, layers: visibleLayers } = printingSettings;
            
            // Формируем поле текста на основе выбранных полей
            const getTextField = (fields) => {
                if (!fields || fields.length === 0) return ['coalesce', ['get', 'name'], ['get', 'cadastral_number'], ''];
                const parts = [];
                fields.forEach((field, i) => {
                    parts.push(['coalesce', ['to-string', ['get', field]], '']);
                    if (i < fields.length - 1) parts.push('\n');
                });
                return ['concat', ...parts];
            };

            const textField = getTextField(text.fields);

            // Скрываем основные слои если они выключены в настройках печати
            layersConfig.forEach(layer => {
                if (layer.id.startsWith('roads') && !visibleLayers.roads) layer.layout = { ...layer.layout, visibility: 'none' };
                if (layer.id.startsWith('buildings') && !visibleLayers.buildings) layer.layout = { ...layer.layout, visibility: 'none' };
                if (layer.id.startsWith('plots') && !visibleLayers.plots) layer.layout = { ...layer.layout, visibility: 'none' };
                if (layer.id.startsWith('markers') && !visibleLayers.objects) layer.layout = { ...layer.layout, visibility: 'none' };
            });

            // Добавляем слои с подписями
            if (text.showPlots) {
                layersConfig.push(getPlotLabelLayer(plotConditions, { 
                    offset: text.plotOffset, 
                    size: text.plotSize,
                    textField: textField 
                }));
            }
            if (text.showBuildings) {
                layersConfig.push(getBuildingLabelLayer(buildingConditions, { 
                    offset: text.buildingOffset, 
                    size: text.buildingSize,
                    textField: textField 
                }));
            }
        }
        // ------------------------------------------

        // Слой-перехватчик для режима рисования.
        // Добавляется последним, чтобы быть поверх всех.
        if (isDrawing) {
            layersConfig.push({
                id: 'drawing-interceptor-layer',
                type: 'fill',
                source: 'park-data', // Можно использовать любой существующий источник
                filter: ['==', '$type', 'Polygon'], // Фильтр, который ничего не найдет, чтобы слой был пустым
                paint: {
                    'fill-color': 'transparent' // Полностью прозрачный
                }
            });
        }



        // Слой для отображения ИСХОДНОЙ геометрии при редактировании (полупрозрачный)
        if (featureBeforeEdit) {
            layersConfig.push({
                id: 'editing-geometry-layer',
                type: 'fill',
                source: 'editing-geometry-source', // Используем отдельный источник
                paint: {
                    'fill-color': '#fbb03b',
                    'fill-opacity': 0.3,
                    'fill-outline-color': '#fbb03b'
                }
            });
        }

        // Отображение нового нарисованного объекта (еще не сохраненного в mapDataProp)
        if (focusedFeature && isNewFeature) {
            const fType = focusedFeature.properties?.type;
            const geomType = focusedFeature.geometry?.type;

            if (fType === 'plot' || (!fType && (geomType === 'Polygon' || geomType === 'MultiPolygon'))) {
                layersConfig.push(...getPlotLayer([], 'focused-feature-source'));
            } else if (fType === 'building') {
                layersConfig.push(...getBuildingLayer([], '3d', 'focused-feature-source'));
            } else if (fType === 'line' || fType === 'road' || (!fType && (geomType === 'LineString' || geomType === 'MultiLineString'))) {
                layersConfig.push(...getRoadLayer([], 'focused-feature-source'));
            } else if (fType === 'marker' || (!fType && (geomType === 'Point' || geomType === 'MultiPoint'))) {
                layersConfig.push(getMarkerLayer([], 'focused-feature-source'));
            }
        }

        // Слой для подсветки МНОЖЕСТВЕННОГО выделения (Shift+Click)
        if (isShiftSelectionEnabled && mapSelectedIds && mapSelectedIds.length > 0) {
            const multiFilter = ['match', ['get', 'id'], mapSelectedIds, true, false];
            layersConfig.push({
                id: 'multi-highlight-layer-fill',
                type: 'fill',
                source: 'park-data',
                filter: ['all', ['in', '$type', 'Polygon', 'MultiPolygon'], multiFilter],
                paint: {
                    'fill-color': '#007bff',
                    'fill-opacity': 0.3
                }
            });
            layersConfig.push({
                id: 'multi-highlight-layer-outline',
                type: 'line',
                source: 'park-data',
                filter: multiFilter,
                paint: {
                    'line-color': '#007bff',
                    'line-width': 2,
                    'line-opacity': 0.8
                }
            });
            layersConfig.push({
                id: 'multi-highlight-layer-circle',
                type: 'circle',
                source: 'park-data',
                filter: ['all', ['==', '$type', 'Point'], multiFilter],
                paint: {
                    'circle-radius': 8,
                    'circle-color': 'transparent',
                    'circle-stroke-color': '#007bff',
                    'circle-stroke-width': 2
                }
            });
        }

        // Слой для подсветки ВЫБРАННОГО объекта (когда открыта панель свойств)
        // Добавляем В КОНЕЦ массива, чтобы слой был поверх всех остальных
        if (focusedFeature) {
            const highlightSource = isNewFeature ? 'focused-feature-source' : 'park-data';
            const highlightFilter = isNewFeature ? ['all'] : ['==', ['get', 'id'], focusedFeature.id];
            const geomType = focusedFeature.geometry?.type;

            if (['Polygon', 'MultiPolygon'].includes(geomType)) {
                layersConfig.push({
                    id: 'highlight-layer-fill',
                    type: 'fill',
                    source: highlightSource,
                    filter: highlightFilter,
                    paint: {
                        'fill-color': '#fbb03b',
                        'fill-opacity': 0.4
                    }
                });
                layersConfig.push({
                    id: 'highlight-layer-outline',
                    type: 'line',
                    source: highlightSource,
                    filter: highlightFilter,
                    paint: {
                        'line-color': '#fbb03b',
                        'line-width': 3,
                        'line-opacity': 0.9
                    }
                });
            } else if (['LineString', 'MultiLineString'].includes(geomType)) {
                layersConfig.push({
                    id: 'highlight-layer-outline',
                    type: 'line',
                    source: highlightSource,
                    filter: highlightFilter,
                    paint: {
                        'line-color': '#fbb03b',
                        'line-width': 6,
                        'line-opacity': 0.9
                    }
                });
            } else if (['Point', 'MultiPoint'].includes(geomType)) {
                layersConfig.push({
                    id: 'highlight-layer-circle',
                    type: 'circle',
                    source: highlightSource,
                    filter: highlightFilter,
                    paint: {
                        'circle-radius': 12,
                        'circle-color': '#fbb03b',
                        'circle-opacity': 0.6,
                        'circle-stroke-width': 3,
                        'circle-stroke-color': '#fbb03b'
                    },
                    beforeId: 'markers-layer'
                });
            }
        }

        // Слой предпросмотра дублируемого объекта
        if (duplicatePlacement && duplicatePlacement.translatedFeature) {
            const dupGeomType = duplicatePlacement.translatedFeature.geometry?.type;
            const isPoly = ['Polygon', 'MultiPolygon'].includes(dupGeomType);
            const isLine = ['LineString', 'MultiLineString'].includes(dupGeomType);
            const isPoint = ['Point', 'MultiPoint'].includes(dupGeomType);

            if (isPoly) {
                layersConfig.push({
                    id: 'duplicate-preview-fill',
                    type: 'fill',
                    source: 'duplicate-preview-source',
                    paint: {
                        'fill-color': duplicatePlacement.isSnapped ? '#10b981' : '#3b82f6',
                        'fill-opacity': 0.45
                    }
                });
                layersConfig.push({
                    id: 'duplicate-preview-stroke',
                    type: 'line',
                    source: 'duplicate-preview-source',
                    paint: {
                        'line-color': duplicatePlacement.isSnapped ? '#059669' : '#2563eb',
                        'line-width': 2.5,
                        'line-dasharray': duplicatePlacement.isSnapped ? [1, 0] : [2, 1]
                    }
                });
            } else if (isLine) {
                layersConfig.push({
                    id: 'duplicate-preview-line',
                    type: 'line',
                    source: 'duplicate-preview-source',
                    paint: {
                        'line-color': duplicatePlacement.isSnapped ? '#10b981' : '#3b82f6',
                        'line-width': 4,
                        'line-dasharray': [2, 1]
                    }
                });
            } else if (isPoint) {
                layersConfig.push({
                    id: 'duplicate-preview-circle',
                    type: 'circle',
                    source: 'duplicate-preview-source',
                    paint: {
                        'circle-radius': 10,
                        'circle-color': duplicatePlacement.isSnapped ? '#10b981' : '#3b82f6',
                        'circle-opacity': 0.7,
                        'circle-stroke-width': 2,
                        'circle-stroke-color': '#ffffff'
                    }
                });
            }
        }

        return layersConfig;
    }, [mapDataProp, plotConditions, buildingConditions, roadConditions, markerConditions, statusConditions, statusIconsSource, showStatusIcons, focusedFeature, isNewFeature, featureBeforeEdit, mapSelectedIds, duplicatePlacement]);

    // Источники данных
    const sources = useMemo(() => {
        const sources = {};

        if (mapDataProp) {
            sources['park-data'] = {
                type: 'geojson',
                promoteId: 'id',
                data: {
                    type: 'FeatureCollection',
                    features: mapDataProp.features.filter(
                        f => f.properties.visible !== false && f.id !== featureToEditGeometry?.id
                    )
                 }
            };
        }

        if (statusIconsSource && statusIconsSource.features.length > 0) {
            sources['status-icons-source'] = {
                type: 'geojson',
                data: statusIconsSource
            };
        }

        // Этот источник используется для старой геометрии (полупрозрачной) при редактировании
        if (featureBeforeEdit) {
            sources['editing-geometry-source'] = {
                type: 'geojson',
                data: featureBeforeEdit
            };
        }

        // Источник для нового или редактируемого объекта (когда он в фокусе/настройке)
        if (focusedFeature) {
            sources['focused-feature-source'] = {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [focusedFeature]
                }
            };
        }

        // Источники для дублируемого объекта
        if (duplicatePlacement && duplicatePlacement.translatedFeature) {
            sources['duplicate-preview-source'] = {
                type: 'geojson',
                data: duplicatePlacement.translatedFeature
            };
        }

        return sources;
    }, [mapDataProp, statusIconsSource, featureBeforeEdit, focusedFeature, featureToEditGeometry, duplicatePlacement]);

    // Определяем, какие слои должны быть интерактивными для клика (выбора)
    const interactiveClickLayerIds = useMemo(() => {
        if (duplicatingFeature) {
            return [];
        }
        if (featureToEditGeometry || isDrawing || (activeTool && activeTool.startsWith('draw-'))) {
            return isDrawing ? ['drawing-interceptor-layer'] : [];
        }
        // Мы хотим выбирать только основные объекты, а не их подсветку или иконки статусов
        const baseLayers = layers.map(l => l.id).filter(id => !id.startsWith('highlight-') && !id.startsWith('status-icons') && !id.startsWith('duplicate-'));
        return baseLayers;
    }, [layers, isDrawing, featureToEditGeometry, activeTool, duplicatingFeature]);

    return (
        <div className={styles.mapContainer}>
            <Map
                ref={mapRef}
                initialViewState={initialViewState}
                mapStyle={mapStyle}
                onLoad={onMapLoad}
                onClick={handleMapClick}
                onMouseMove={handleMapMouseMove}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMoveEnd={(evt) => onMapMove && onMapMove(evt.viewState)}
                onTouchEnd={handleFeatureTap}
                interactiveLayerIds={interactiveClickLayerIds}
                attributionControl={false}
                preserveDrawingBuffer={true}
            >
                {/* Источник и слой для подложки рендерятся отдельно для управления порядком */}
                {overlay && (
                    <Source id="overlay-source" type="image" url={overlay.image_url} coordinates={overlay.coordinates}>
                        <Layer
                            id="overlay-layer"
                            type="raster"
                            paint={{ 'raster-opacity': overlay.opacity ?? 0.75 }}
                            beforeId="plots-layer-solid" // Отображаем подложку под всеми объектами
                        />
                    </Source>
                )}
                {/* Рендер источников и слоев */}
                {Object.entries(sources).map(([id, source]) => (
                    <Source key={id} id={id} {...source} />
                ))}

                {layers.map(layer => (
                    <Layer key={layer.id} {...layer} />
                ))}

                {/* ОВЕРЛЕИ ЛЕГЕНДЫ (ДЛЯ ПЕЧАТИ) */}
                {printingSettings && printingSettings.legend.visible && LegendOverlay && (
                    <>
                        <LegendOverlay settings={printingSettings.legend} mapData={mapDataProp} position="top" />
                        <LegendOverlay settings={printingSettings.legend} mapData={mapDataProp} position="bottom" />
                        <LegendOverlay settings={printingSettings.legend} mapData={mapDataProp} position="left" />
                        <LegendOverlay settings={printingSettings.legend} mapData={mapDataProp} position="right" />
                    </>
                )}

                <NavigationControl position="top-right" />
                <AttributionControl 
                    customAttribution='<a href="https://store.ichis.ru/#IchisSPD" target="_blank" rel="noopener noreferrer">IChis SPD</a>'
                    position="bottom-right" 
                />

                {/* Кастомные контролы */}
                {styleSwapControlProps && (
                    <StyleSwapControlWrapper {...styleSwapControlProps} />
                )}
            </Map>

            {/* Оверлей легенды для печати */}
            {printingSettings?.legend?.visible && (
                <div className={`${styles.legendOverlay} ${styles[printingSettings.legend.position]}`}>
                    <div className={styles.legendTitle}>{printingSettings.legend.title}</div>
                    <div className={styles.legendItems}>
                        {legendData.plots?.slice(0, 8).map(item => (
                            <div key={item.id} className={styles.legendItem}>
                                <div className={styles.colorBox} style={{ backgroundColor: item.color }} />
                                <span>{item.label}</span>
                            </div>
                        ))}
                        {legendData.buildings?.slice(0, 4).map(item => (
                            <div key={item.id} className={styles.legendItem}>
                                <div className={styles.colorBox} style={{ backgroundColor: item.color }} />
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Popup с информацией об объекте */}
            {popupInfo && (
                <Popup
                    longitude={popupInfo.coordinates.lng}
                    latitude={popupInfo.coordinates.lat}
                    anchor="bottom"
                    onClose={() => setPopupInfo(null)}
                    closeOnClick={false}
                >
                    <div className={styles.popupContent}>
                        <h4>{popupInfo.properties.name || popupInfo.properties.cadastral_number || 'Объект'}</h4>
                        <p><strong>Тип:</strong> {popupInfo.properties.type}</p>
                        {popupInfo.properties.area && <p><strong>Площадь:</strong> {popupInfo.properties.area} м²</p>}
                        {popupInfo.properties.height && <p><strong>Высота:</strong> {popupInfo.properties.height} м</p>}
                        {popupInfo.properties.owner && <p><strong>Владелец:</strong> {popupInfo.properties.owner}</p>}
                    </div>
                </Popup>
            )}

            {/* Рендеринг панелей из зарегистрированных модулей */}
            {registryModules.map(module => {
                if (module.getPanels && mapRef.current) {
                    const panels = module.getPanels(mapRef.current.getMap());
                    if (Array.isArray(panels)) {
                        return panels.map(panel => {
                            const PanelComponent = panel.component;
                            return <PanelComponent key={panel.id} {...panel.props} />;
                        });
                    }
                }
                return null;
            })}
        </div>
    );
};

const StyleSwapControlWrapper = (props) => {
    const ctrl = useControl(() => new StyleSwapControl(props), { position: 'top-right' });
    // Обновляем контрол при изменении пропсов
    ctrl.update(props);
    return null;
};


export default MapViewer;
